import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import React from "react"
import ClearIcon from "@mui/icons-material/Clear"

import { z } from "zod"
import { DevTool } from "@/components/FormDevTool"
import { useAddModel } from "@/hooks/useAddModel"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { isAdminPortal } from "@/utilities/domain"

const StoreSchema = z.object({
  name: z.string().min(3),
  email: z.string().email(),
  phone: z.string().min(10),
  branch_ids: z.array(z.number()).optional(),
  role_names: z.array(z.string()).min(1, "Select at least one role"),
})

const AddStoreModal = ({ open, onClose }) => {
  const canChooseBranches = isAdminPortal()
  const {
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    control,
    Controller,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    defaultOnClose,
    setValue,
  } = useAddModel("user", StoreSchema, undefined, undefined, (data) => {
    const { branch_ids, ...branchUser } = data
    return canChooseBranches ? data : branchUser
  })
  // The API returns a user's roles as `roles`, while the form field is
  // `role_names`, so the generic row-to-form sync cannot fill it.
  React.useEffect(() => {
    setValue("role_names", selectedRow?.roles ?? [])
  }, [selectedRow, setValue])

  const { data: branchResponse } = useCustomQuery(
    ["branches"],
    "branches/all",
    {
      enabled: canChooseBranches,
    }
  )
  const { data: roleResponse } = useCustomQuery(["roles"], "role/all")
  const branches = Array.isArray(branchResponse)
    ? branchResponse
    : (branchResponse?.data ?? [])
  const roles = Array.isArray(roleResponse)
    ? roleResponse
    : (roleResponse?.data ?? [])

  return (
    <Modal
      open={open ? open : openDialog}
      onClose={defaultOnClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          width: 800,
          margin: "100px auto",
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
          padding: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update User" : "Add New User"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "500",
              fontSize: { xs: "12px", md: "16px" },
              marginBottom: 2,
            }}
            className="text-black"
          >
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register("name")}
          />
          <TextField
            fullWidth
            label="Email"
            sx={{ marginBottom: 2 }}
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register("email")}
          />
          <TextField
            fullWidth
            label="Mobile Number"
            sx={{ marginBottom: 2 }}
            error={!!errors.phone}
            helperText={errors.phone?.message}
            {...register("phone")}
          />
          {canChooseBranches && (
            <Controller
              name="branch_ids"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                  <InputLabel id="user-branches-label">Branches</InputLabel>
                  <Select
                    {...field}
                    multiple
                    labelId="user-branches-label"
                    label="Branches"
                    value={field.value ?? []}
                    onChange={(event) => field.onChange(event.target.value)}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}
          <Controller
            name="role_names"
            control={control}
            defaultValue={selectedRow?.roles ?? []}
            render={({ field }) => (
              <FormControl
                fullWidth
                sx={{ marginBottom: 2 }}
                error={!!errors.role_names}
              >
                <InputLabel id="user-roles-label">Roles</InputLabel>
                <Select
                  {...field}
                  multiple
                  labelId="user-roles-label"
                  label="Roles"
                  value={field.value ?? []}
                  onChange={(event) => field.onChange(event.target.value)}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_names && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {errors.role_names.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Divider sx={{ marginBottom: 2 }} />
          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "10px",
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>

              {/* remove this after form completion */}
              <DevTool control={control}></DevTool>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddStoreModal
